// reservebar-config.js
import Liquid from '@liquidcloud/sdk';

export const reservebarConfig =
	typeof window !== 'undefined' && window.reservebarPopupConfig
		? window.reservebarPopupConfig
		: {};

export function getReservebarLabel(key, fallback) {
	if (
		reservebarConfig &&
		Object.prototype.hasOwnProperty.call(reservebarConfig, key) &&
		typeof reservebarConfig[key] === 'string' &&
		reservebarConfig[key].trim() !== ''
	) {
		return reservebarConfig[key];
	}
	return fallback;
}

/** Grouping IDs that show "Sold out." instead of the default unavailable message. */
export const RESERVEBAR_SOLD_OUT_GROUPING_IDS = new Set(['GROUPING-2569068']);

const RESERVEBAR_UNAVAILABLE_MESSAGE_FALLBACK =
	"We're sorry, this product is not sold by ReserveBar at nearby vendors and unavailable to ship to your state";

export function normalizeLiquidProduct(products, groupingId) {
	if (!products) {
		return null;
	}

	const id = groupingId ? String(groupingId).trim() : '';

	if (Array.isArray(products)) {
		if (id) {
			const match = products.find(
				(product) =>
					product &&
					(product.id === id ||
						product.groupingId === id ||
						product.grouping_id === id)
			);
			if (match) {
				return match;
			}
		}
		return products[0] || null;
	}

	if (Array.isArray(products.products)) {
		return products.products[0] || null;
	}

	if (typeof products.get === 'function') {
		if (id && products.has(id)) {
			return products.get(id);
		}
		const next = products.values().next();
		return next.done ? null : next.value;
	}

	if (typeof products === 'object') {
		if (id && products[id]) {
			return products[id];
		}

		const values = Object.values(products).filter(
			(product) =>
				product &&
				typeof product === 'object' &&
				Array.isArray(product.variants)
		);

		if (id) {
			const match = values.find((product) => product.id === id);
			if (match) {
				return match;
			}
		}

		return values[0] || null;
	}

	return null;
}

/** @typedef {'standard' | 'pre_sale' | 'back_order'} LiquidCartCustomerPlacement */

export function isPresalePlacement(placement) {
	const normalized = String(placement || '')
		.toLowerCase()
		.replace(/_/g, '');
	return normalized === 'presale';
}

export function variantHasActivePresale(variant) {
	return !!(variant && variant.presale && variant.presale.active);
}

export function getRetailerCustomerPlacement(retailer, variant) {
	if (retailer && retailer.customerPlacement) {
		return retailer.customerPlacement;
	}
	if (variantHasActivePresale(variant)) {
		return 'pre_sale';
	}
	return 'standard';
}

/**
 * Format for liquidSDK.cart.add().
 * @see https://liquidapp.co/docs/sdk/cart — customerPlacement: standard | pre_sale | back_order
 */
export function normalizeCustomerPlacementForCart(placement, variant) {
	if (variantHasActivePresale(variant)) {
		return 'pre_sale';
	}

	const raw = placement || getRetailerCustomerPlacement(null, variant);
	const normalized = String(raw).toLowerCase().replace(/_/g, '');

	if (normalized === 'presale') {
		return 'pre_sale';
	}
	if (normalized === 'backorder') {
		return 'back_order';
	}

	return 'standard';
}

/** @deprecated Use normalizeCustomerPlacementForCart */
export function normalizeCustomerPlacement(placement, variant) {
	return normalizeCustomerPlacementForCart(placement, variant);
}

function retailerPlacementRank(retailer, variant) {
	const placement = getRetailerCustomerPlacement(retailer, variant);
	if (isPresalePlacement(placement)) {
		return 0;
	}
	const normalized = String(placement).toLowerCase().replace(/_/g, '');
	if (normalized === 'backorder') {
		return 1;
	}
	return 2;
}

export function isRetailerPurchasable(retailer, variant) {
	if (!retailer || !retailer.shippingMethod) {
		return false;
	}
	if (retailer.inStock !== 0) {
		return true;
	}
	if (variantHasActivePresale(variant)) {
		return true;
	}
	return isPresalePlacement(getRetailerCustomerPlacement(retailer, variant));
}

export function getReservebarUnavailableMessage(groupingId) {
	const id = String(groupingId || '').trim();

	if (RESERVEBAR_SOLD_OUT_GROUPING_IDS.has(id)) {
		return 'Sold out.';
	}

	return getReservebarLabel(
		'reservebar_cart_unavailable_message',
		RESERVEBAR_UNAVAILABLE_MESSAGE_FALLBACK
	);
}

export function selectPreferredRetailer(retailers, variant) {
	if (!Array.isArray(retailers) || !retailers.length) {
		return null;
	}

	const available = retailers.filter((retailer) =>
		isRetailerPurchasable(retailer, variant)
	);

	if (!available.length) {
		return null;
	}

	if (variantHasActivePresale(variant)) {
		const presaleRetailer = available.find((retailer) =>
			isPresalePlacement(
				getRetailerCustomerPlacement(retailer, variant)
			)
		);
		if (presaleRetailer) {
			return presaleRetailer;
		}
	}

	return [...available].sort(
		(a, b) =>
			retailerPlacementRank(a, variant) -
			retailerPlacementRank(b, variant)
	)[0];
}

export function getDeliveryExpectationSubtitle(retailer, variant) {
	if (variantHasActivePresale(variant) && variant.presale?.expectedDeliveryDate) {
		const date = new Date(variant.presale.expectedDeliveryDate);
		if (!isNaN(date.getTime())) {
			return `Ships ${date.toLocaleDateString('en-US', {
				month: 'long',
				year: 'numeric'
			})}`;
		}
	}

	const shippingMethod = retailer?.shippingMethod;
	if (shippingMethod) {
		return (
			shippingMethod.shortDeliveryExpectation ||
			shippingMethod.deliveryExpectation ||
			shippingMethod.desc?.expected ||
			''
		);
	}

	return '';
}

export function getRetailerPlacementRank(retailer, variant) {
	if (variantHasActivePresale(variant)) {
		return 0;
	}
	return retailerPlacementRank(retailer, variant);
}

let liquidClientPromise = null;
export function getLiquidClient() {
	if (!liquidClientPromise) {
		liquidClientPromise = Liquid({
			clientId: 'cc615190c9d297b935482113af23d8b1',
			env: 'production'
		});
	}
	return liquidClientPromise;
}

export function getCookie(name) {
	if (typeof document === 'undefined') return '';
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(';').shift();
	return '';
}

export function getLiquidAddressString() {
	try {
		const raw = getCookie('_liquid_address');
		if (!raw) return '';
		const decoded = decodeURIComponent(raw);
		try {
			const obj = JSON.parse(decoded);
			const lineParts = [];
			if (obj.address1) lineParts.push(obj.address1);
			if (obj.address2) lineParts.push(obj.address2);
			const cityLineParts = [];
			if (obj.city) cityLineParts.push(obj.city);
			if (obj.state || obj.region)
				cityLineParts.push(obj.state || obj.region);
			if (obj.postalCode || obj.zip)
				cityLineParts.push(obj.postalCode || obj.zip);
			const lines = [];
			if (lineParts.length) lines.push(lineParts.join(' '));
			if (cityLineParts.length) lines.push(cityLineParts.join(', '));
			return lines.join(', ');
		} catch (e) {
			return decoded;
		}
	} catch (e) {
		return '';
	}
}

export function getLiquidAddressObject() {
	try {
		const raw = getCookie('_liquid_address');
		if (!raw) return null;
		const decoded = decodeURIComponent(raw);
		try {
			return JSON.parse(decoded);
		} catch (e) {
			return null;
		}
	} catch (e) {
		return null;
	}
}

/**
 * Keep the Liquid cart SDK address state in sync with our cookie.
 * Checkout stays disabled until liquidSDK.address has a value.
 */
export function syncLiquidSdkAddress(place, {resetCart = false} = {}) {
	if (!place || typeof document === 'undefined') {
		return;
	}

	try {
		const encoded = encodeURIComponent(JSON.stringify(place));
		document.cookie = `_liquid_address=${encoded}; path=/; max-age=31536000`;
	} catch (e) {
		console.error('[ReserveBar] failed to set _liquid_address cookie', e);
	}

	try {
		if (
			typeof liquidSDK !== 'undefined' &&
			liquidSDK.address &&
			typeof liquidSDK.address.set === 'function'
		) {
			liquidSDK.address.set(place, resetCart);
		}
	} catch (e) {
		console.warn('[ReserveBar] failed to sync liquidSDK address', e);
	}
}

export function syncLiquidSdkAddressFromCookie({resetCart = false} = {}) {
	const place = getLiquidAddressObject();
	if (place) {
		syncLiquidSdkAddress(place, {resetCart});
	}
}

// ---- Simple global store shared with Vue ----

export function getReservebarStore() {
	const STORE_NS = 'reservebarStore';
	const TTL_MS = 60 * 60 * 1000; // 1 hour
	if (typeof window === 'undefined') return null;

	function pruneExpired(map) {
		const now = Date.now();
		let changed = false;
		Object.keys(map).forEach((key) => {
			const entry = map[key];
			if (!entry || typeof entry !== 'object') {
				delete map[key];
				changed = true;
				return;
			}
			const savedAt =
				typeof entry.savedAt === 'number' ? entry.savedAt : 0;
			if (!savedAt || now - savedAt > TTL_MS) {
				delete map[key];
				changed = true;
			}
		});
		return changed;
	}

	if (!window[STORE_NS]) {
		window[STORE_NS] = {
			products: {}, // key => { productId, variantId, price, retailer, shippingMethod, raw }
			addresses: {}, //optional, if you want to store address keys as well
			setProduct(key, payload) {
				const entry = {
					data: payload,
					savedAt: Date.now()
				};
				this.products[key] = entry;
				try {
					localStorage.setItem(
						'reservebar_products',
						JSON.stringify(this.products)
					);
				} catch (e) {
					console.warn('LS save error', e);
				}
			},
			getProduct(key) {
				const entry = this.products[key];
				if (!entry) return null;

				const now = Date.now();
				const savedAt =
					typeof entry.savedAt === 'number' ? entry.savedAt : 0;
				if (!savedAt || now - savedAt > TTL_MS) {
					delete this.products[key];
					try {
						localStorage.setItem(
							'reservebar_products',
							JSON.stringify(this.products)
						);
					} catch (e) {
						console.warn('LS save error', e);
					}
					return null;
				}

				// Support old format where value was stored directly
				if (!Object.prototype.hasOwnProperty.call(entry, 'data')) {
					return entry;
				}

				return entry.data;
			},
			setAddress(key, payload) {
				const entry = {
					data: payload,
					savedAt: Date.now()
				};
				this.addresses[key] = entry;
				try {
					localStorage.setItem(
						'reservebar_addresses',
						JSON.stringify(this.addresses)
					);
				} catch (e) {
					console.warn('LS save error', e);
				}
			},
			getAddress(key) {
				const entry = this.addresses[key];
				if (!entry) return null;

				const now = Date.now();
				const savedAt =
					typeof entry.savedAt === 'number' ? entry.savedAt : 0;
				if (!savedAt || now - savedAt > TTL_MS) {
					delete this.addresses[key];
					try {
						localStorage.setItem(
							'reservebar_addresses',
							JSON.stringify(this.addresses)
						);
					} catch (e) {
						console.warn('LS save error', e);
					}
					return null;
				}

				// Support old format where value was stored directly
				if (!Object.prototype.hasOwnProperty.call(entry, 'data')) {
					return entry;
				}

				return entry.data;
			}
		};
		// Load persisted data from localStorage
		try {
			const savedProducts = localStorage.getItem('reservebar_products');
			if (savedProducts) {
				const parsed = JSON.parse(savedProducts);
				if (parsed && typeof parsed === 'object') {
					Object.keys(parsed).forEach((key) => {
						const val = parsed[key];
						if (
							val &&
							typeof val === 'object' &&
							Object.prototype.hasOwnProperty.call(val, 'data')
						) {
							window[STORE_NS].products[key] = val;
						} else {
							window[STORE_NS].products[key] = {
								data: val,
								savedAt: Date.now()
							};
						}
					});
					if (pruneExpired(window[STORE_NS].products)) {
						localStorage.setItem(
							'reservebar_products',
							JSON.stringify(window[STORE_NS].products)
						);
					}
				}
			}

			const savedAddresses = localStorage.getItem('reservebar_addresses');
			if (savedAddresses) {
				const parsedAddr = JSON.parse(savedAddresses);
				if (parsedAddr && typeof parsedAddr === 'object') {
					Object.keys(parsedAddr).forEach((key) => {
						const val = parsedAddr[key];
						if (
							val &&
							typeof val === 'object' &&
							Object.prototype.hasOwnProperty.call(val, 'data')
						) {
							window[STORE_NS].addresses[key] = val;
						} else {
							window[STORE_NS].addresses[key] = {
								data: val,
								savedAt: Date.now()
							};
						}
					});
					if (pruneExpired(window[STORE_NS].addresses)) {
						localStorage.setItem(
							'reservebar_addresses',
							JSON.stringify(window[STORE_NS].addresses)
						);
					}
				}
			}
		} catch (e) {
			console.warn('LS load error', e);
		}
	}

	return window[STORE_NS];
}

// key = product + state + zip (you can adjust it to suit your case)
export function createProductStoreKey(gid, addrObj) {
	if (!gid) return null;

	const state =
		(addrObj && (addrObj.state || addrObj.region || addrObj.stateCode)) ||
		'';
	const zip =
		(addrObj && (addrObj.postalCode || addrObj.zip || addrObj.zipCode)) ||
		'';

	return `${String(gid)}::${state}::${zip}`;
}

export function buildAddressParams(addrObj) {
	let shipAddress = '';
	let shipState = '';
	let latitude = null;
	let longitude = null;

	if (addrObj) {
		const parts = [];
		if (addrObj.address1) parts.push(addrObj.address1);
		if (addrObj.address2) parts.push(addrObj.address2);

		const cityLine = [];
		if (addrObj.city) cityLine.push(addrObj.city);
		if (addrObj.state || addrObj.region)
			cityLine.push(addrObj.state || addrObj.region);
		if (addrObj.postalCode || addrObj.zip)
			cityLine.push(addrObj.postalCode || addrObj.zip);

		if (cityLine.length) parts.push(cityLine.join(', '));
		shipAddress = parts.join(', ');

		shipState = addrObj.state || addrObj.region || addrObj.stateCode || '';

		latitude = addrObj.latitude || addrObj.lat || null;
		longitude = addrObj.longitude || addrObj.lng || null;
	}

	return {shipAddress, shipState, latitude, longitude};
}
